/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * The economy + progression layer for the "Living World Garden" grow loop.
 *
 * Flowerbed ships a rich set of plant meshes but no game economy. This module
 * assigns each plant *family* a cost (to plant), a sell value (on harvest), a
 * rarity tier, and a real-world growth duration. Growth is measured against the
 * wall clock (see PlantGrowingSystem), so a plant keeps ripening while the
 * player is away.
 *
 * Seedbox "plant groups" are keyed by variant strings such as `sunflower_a`,
 * `oak`, or `pansy_C`. We collapse those to a family (`sunflower`, `oak`,
 * `pansy`) so every colour variant of a flower shares one economic profile.
 */

export const RARITY = {
	COMMON: 'common',
	UNCOMMON: 'uncommon',
	RARE: 'rare',
	EPIC: 'epic',
	LEGENDARY: 'legendary',
};

/** Display colour per rarity, used by the HUD/shop. */
export const RARITY_COLOR = {
	[RARITY.COMMON]: '#b8c4b0',
	[RARITY.UNCOMMON]: '#5ec26a',
	[RARITY.RARE]: '#3aa0ff',
	[RARITY.EPIC]: '#b45cff',
	[RARITY.LEGENDARY]: '#ffb01f',
};

const MINUTES = 60 * 1000;

/**
 * @typedef {{
 *   displayName: string,
 *   cost: number,
 *   sellValue: number,
 *   rarity: string,
 *   growthDuration: number, // real time, milliseconds
 * }} SeedInfo
 */

/** Economic profile per plant family. @type {Object<string, SeedInfo>} */
export const SEED_FAMILIES = {
	pansy: {
		displayName: 'Pansy',
		cost: 5,
		sellValue: 12,
		rarity: RARITY.COMMON,
		growthDuration: 1 * MINUTES,
	},
	tulip: {
		displayName: 'Tulip',
		cost: 6,
		sellValue: 15,
		rarity: RARITY.COMMON,
		growthDuration: 1.5 * MINUTES,
	},
	daffodil: {
		displayName: 'Daffodil',
		cost: 6,
		sellValue: 15,
		rarity: RARITY.COMMON,
		growthDuration: 1.5 * MINUTES,
	},
	nasturtium: {
		displayName: 'Nasturtium',
		cost: 10,
		sellValue: 26,
		rarity: RARITY.UNCOMMON,
		growthDuration: 2 * MINUTES,
	},
	carnation: {
		displayName: 'Carnation',
		cost: 10,
		sellValue: 26,
		rarity: RARITY.UNCOMMON,
		growthDuration: 2 * MINUTES,
	},
	allium: {
		displayName: 'Allium',
		cost: 12,
		sellValue: 30,
		rarity: RARITY.UNCOMMON,
		growthDuration: 2.5 * MINUTES,
	},
	lavender: {
		displayName: 'Lavender',
		cost: 12,
		sellValue: 32,
		rarity: RARITY.UNCOMMON,
		growthDuration: 2.5 * MINUTES,
	},
	succulent: {
		displayName: 'Succulent',
		cost: 18,
		sellValue: 48,
		rarity: RARITY.RARE,
		growthDuration: 3 * MINUTES,
	},
	rose: {
		displayName: 'Rose',
		cost: 20,
		sellValue: 55,
		rarity: RARITY.RARE,
		growthDuration: 3.5 * MINUTES,
	},
	sunflower: {
		displayName: 'Sunflower',
		cost: 22,
		sellValue: 60,
		rarity: RARITY.RARE,
		growthDuration: 4 * MINUTES,
	},
	fir: {
		displayName: 'Cedar',
		cost: 35,
		sellValue: 100,
		rarity: RARITY.EPIC,
		growthDuration: 6 * MINUTES,
	},
	cherryblossom: {
		displayName: 'Cherry Blossom',
		cost: 40,
		sellValue: 120,
		rarity: RARITY.EPIC,
		growthDuration: 6 * MINUTES,
	},
	sugarpine: {
		displayName: 'Sugar Pine',
		cost: 45,
		sellValue: 130,
		rarity: RARITY.EPIC,
		growthDuration: 7 * MINUTES,
	},
	oak: {
		displayName: 'Oak',
		cost: 60,
		sellValue: 200,
		rarity: RARITY.LEGENDARY,
		growthDuration: 10 * MINUTES,
	},
};

const DEFAULT_SEED_INFO = {
	displayName: 'Seed',
	cost: 5,
	sellValue: 10,
	rarity: RARITY.COMMON,
	growthDuration: 1 * MINUTES,
};

/**
 * Signature species per world region — the "Living World" hook. A player's
 * region (guessed from their locale/timezone) unlocks and highlights its
 * signature flower. Keys are ISO-ish region hints matched loosely.
 * @type {Object<string, string>}
 */
export const REGION_SIGNATURE = {
	JP: 'cherryblossom',
	NL: 'tulip',
	US: 'sunflower',
	FR: 'lavender',
	GB: 'rose',
	IN: 'nasturtium',
	AU: 'allium',
	CA: 'fir',
	DE: 'carnation',
	BR: 'daffodil',
};

/**
 * Collapse a seedbox plant group / plant type (e.g. `sunflower_a`, `pansy_C`)
 * to its economic family key (e.g. `sunflower`, `pansy`).
 * @param {string} groupOrType
 * @returns {string}
 */
export function seedFamilyOf(groupOrType) {
	if (!groupOrType) return 'pansy';
	return String(groupOrType)
		.toLowerCase()
		.replace(/_[a-z]$/, '');
}

/**
 * Economic profile for a seedbox group / plant type.
 * @param {string} groupOrType
 * @returns {SeedInfo}
 */
export function getSeedInfo(groupOrType) {
	return SEED_FAMILIES[seedFamilyOf(groupOrType)] ?? DEFAULT_SEED_INFO;
}
